// -----------------------------------------------------------------------------
// ScheduleReminderPanel  (Wave A)
//
// Action panel body for SCHEDULE_REMINDER.
// Fields: date+time (default = now +1 day), assignee dropdown, note textarea.
// Submit: dispatch AddReminder thunk, then call onComplete.
// Assignees: sourced from state.practiceUser via FilterAssigneeList (security boundary).
// -----------------------------------------------------------------------------
import PropTypes from 'prop-types';
import { useState, useMemo } from 'react';
import {
    Box,
    Typography,
    Button,
    TextField,
    MenuItem,
    CircularProgress,
    Stack,
} from '@mui/material';
import { dispatch, useSelector } from 'src/redux/store';
import { AddReminder } from 'src/redux/slices/lead/taskThunks';
import { updateError } from 'src/redux/slices/user';
import FilterAssigneeList from 'src/utils/filterAssigneeList';
import Iconify from 'src/components/Iconify';

/** Returns ISO datetime string for now + 1 day, truncated to minute precision. */
function defaultDateTime() {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setSeconds(0);
    d.setMilliseconds(0);
    return d.toISOString().slice(0, 16); // "YYYY-MM-DDTHH:MM"
}

export default function ScheduleReminderPanel({
    item,
    practiceId,
    leadId,
    leadDetail,
    onComplete,
    onClose,
}) {
    const [dateTime, setDateTime] = useState(defaultDateTime);
    const [assigneeId, setAssigneeId] = useState('');
    const [note, setNote] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const rawUsers = useSelector((state) => state.practiceUser?.practiceUser?.results || []);
    const assignees = useMemo(() => FilterAssigneeList(rawUsers), [rawUsers]);

    const handleSubmit = () => {
        if (!dateTime) {
            dispatch(updateError({ success: false, message: 'Please select a date and time' }));
            return;
        }
        setSubmitting(true);

        const treatmentId = leadDetail?.lead_treatments?.[0]?.id || null;

        const data = {
            lead: leadId,
            date: new Date(dateTime).toISOString(),
            group: 'FOLLOW_UP',
            method: 'email',
            ...(treatmentId ? { lead_treatment: treatmentId } : {}),
            ...(assigneeId ? { assignee: assigneeId } : {}),
            ...(note ? { description: note } : {}),
        };

        dispatch(
            AddReminder(data, practiceId, ({ success, message }) => {
                setSubmitting(false);
                if (success) {
                    onComplete?.(item);
                } else {
                    dispatch(updateError({ success: false, message: message || 'Failed to schedule reminder' }));
                }
            })
        );
    };

    return (
        <Box
            data-testid="schedule-reminder-panel"
            sx={{
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.75,
                height: '100%',
                overflowY: 'auto',
            }}
        >
            {/* Icon header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Box
                    sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '10px',
                        bgcolor: 'rgba(255, 171, 0, 0.10)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    <Iconify icon="mdi:bell-outline" width={18} height={18} sx={{ color: '#FFAB00' }} />
                </Box>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1d1d1f', letterSpacing: '-0.012em' }}>
                    Schedule reminder
                </Typography>
            </Box>

            {/* Date + time */}
            <Box>
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#3c3c43', letterSpacing: '-0.005em', mb: 0.5 }}>
                    Remind at
                </Typography>
                <TextField
                    data-testid="schedule-reminder-datetime"
                    type="datetime-local"
                    size="small"
                    fullWidth
                    value={dateTime}
                    onChange={(e) => setDateTime(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                />
            </Box>

            {/* Assignee */}
            {assignees.length > 0 && (
                <Box>
                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#3c3c43', letterSpacing: '-0.005em', mb: 0.5 }}>
                        Assign to
                    </Typography>
                    <TextField
                        data-testid="schedule-reminder-assignee"
                        select
                        size="small"
                        fullWidth
                        value={assigneeId}
                        onChange={(e) => setAssigneeId(e.target.value)}
                        SelectProps={{ displayEmpty: true }}
                    >
                        <MenuItem value="">— Unassigned —</MenuItem>
                        {assignees.map((a) => (
                            <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
                        ))}
                    </TextField>
                </Box>
            )}

            {/* Note */}
            <Box>
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#3c3c43', letterSpacing: '-0.005em', mb: 0.5 }}>
                    Note (optional)
                </Typography>
                <TextField
                    data-testid="schedule-reminder-note"
                    multiline
                    rows={2}
                    fullWidth
                    size="small"
                    placeholder="Add context for this reminder…"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                />
            </Box>

            {/* Actions */}
            <Stack direction="row" spacing={1} sx={{ mt: 'auto' }}>
                <Button
                    data-testid="schedule-reminder-submit"
                    variant="contained"
                    disableElevation
                    onClick={handleSubmit}
                    disabled={submitting || !dateTime}
                    startIcon={submitting ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <Iconify icon="mdi:bell-check-outline" width={14} height={14} />}
                    sx={{
                        flex: 1,
                        textTransform: 'none',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: 600,
                        bgcolor: '#FFAB00',
                        color: '#fff',
                        '&:hover': { bgcolor: '#e09900' },
                        boxShadow: 'none',
                    }}
                >
                    {submitting ? 'Scheduling…' : 'Schedule'}
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={onClose}
                    disabled={submitting}
                    sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '13px' }}
                >
                    Cancel
                </Button>
            </Stack>
        </Box>
    );
}

ScheduleReminderPanel.propTypes = {
    item: PropTypes.object,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadDetail: PropTypes.object,
    onComplete: PropTypes.func,
    onClose: PropTypes.func.isRequired,
};

ScheduleReminderPanel.defaultProps = {
    item: null,
    practiceId: null,
    leadId: null,
    leadDetail: null,
    onComplete: null,
};
